import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Newscomponent from './Newscomponent';
import Spinner from './Spinner';
import InfiniteScroll from 'react-infinite-scroll-component';

const Newsmain = ({ country = 'in', pagesize = 9, category = 'general', setProgress }) => {
    const [searchParams] = useSearchParams();
    const query = (searchParams.get('q') || '').trim();
    const [articles, setArticles] = useState([]);
    const [visibleCount, setVisibleCount] = useState(pagesize);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [provider, setProvider] = useState('');
    const [retry, setRetry] = useState(0);
    const label = category === 'general' ? 'Top Headlines' : `${category[0].toUpperCase()}${category.slice(1)} News`;

    useEffect(() => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 25000);
        let active = true;
        document.title = `${query ? `${query} — ` : ''}${label} | News-Today`;
        setLoading(true);
        setError('');
        setNotice('');
        setArticles([]);
        setVisibleCount(pagesize);
        setProgress(20);

        const load = async () => {
            let serviceError = '';
            try {
                const params = new URLSearchParams({ category, country, q: query });
                const response = await fetch(`/api/news?${params}`, { signal: controller.signal });
                const data = await response.json();
                if (!response.ok) {
                    serviceError = typeof data.error === 'string' ? data.error : 'The news service is temporarily unavailable.';
                    throw new Error(serviceError);
                }
                if (!Array.isArray(data.articles)) {
                    throw new Error('The news service returned an unexpected response.');
                }
                if (active) {
                    const unique = new Map(data.articles.filter((article) => article && article.url && article.title)
                        .map((article) => [article.url, article]));
                    setArticles([...unique.values()]);
                    setNotice(data.notice || '');
                    setProvider(data.provider || '');
                }
            } catch (failure) {
                if (active) {
                    setError(failure.name === 'AbortError'
                        ? 'The news service took too long to respond. Please try again.'
                        : serviceError || 'Unable to load news. Check your connection and try again.');
                }
            } finally {
                clearTimeout(timeout);
                if (active) {
                    setLoading(false);
                    setProgress(100);
                }
            }
        };
        load();
        return () => {
            active = false;
            clearTimeout(timeout);
            controller.abort();
            setProgress(100);
        };
    }, [category, country, query, pagesize, label, setProgress, retry]);

    // RSS is a finite snapshot. Reveal it locally rather than fetching duplicate pages.
    const loadMore = () => setVisibleCount((count) => Math.min(count + pagesize, articles.length));
    const hasMore = visibleCount < articles.length;
    const visibleArticles = articles.slice(0, visibleCount);

    return (
        <main className="container my-3 news-main">
            <h1 className="text-center">News-Today {label}</h1>
            <p className="text-center text-body-secondary">
                {query ? `Results for “${query}” in ${category} news` : 'Latest headlines from the India-English edition'}
            </p>
            {loading && <Spinner />}
            {error && (
                <div className="alert alert-danger text-center" role="alert">
                    <p>{error}</p>
                    <button className="btn btn-primary" onClick={() => setRetry((value) => value + 1)}>Try again</button>
                </div>
            )}
            {!loading && !error && (
                <>
                    {notice && <p className="alert alert-info" role="status">{notice}</p>}
                    {articles.length === 0 ? (
                        <p className="text-center py-5" role="status">
                            {query ? 'No articles found. Try a different search or clear the search box.' : 'No headlines are available right now. Please try again shortly.'}
                        </p>
                    ) : (
                        <>
                            <p className="text-center text-body-secondary" role="status">
                                Showing {visibleArticles.length} of {articles.length} articles · {provider}
                            </p>
                            <InfiniteScroll dataLength={visibleArticles.length} next={loadMore} hasMore={hasMore} loader={null}>
                                <div className="row g-4">
                                    {visibleArticles.map((article) => (
                                        <div className="col-12 col-md-6 col-lg-4" key={article.url}>
                                            <Newscomponent title={article.title} description={article.description}
                                                imgUrl={article.image} newsUrl={article.url} date={article.publishedAt}
                                                source={article.source?.name} />
                                        </div>
                                    ))}
                                </div>
                            </InfiniteScroll>
                            <div className="text-center mt-4">
                                {hasMore ? <button className="btn btn-outline-primary" onClick={loadMore}>Load more</button>
                                    : <p className="text-body-secondary">You’re all caught up.</p>}
                            </div>
                        </>
                    )}
                </>
            )}
        </main>
    );
};

export default Newsmain;
